import { FC, useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Divider, Space, Tag, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { capitalCase } from 'change-case';
import { TbArrowLeft, TbFileArrowRight, TbFileInvoice, TbMailShare } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { EmptySection, LearnMoreFooter } from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';
import PlatformTable from '@/shared/components/tables/PlatformTable';
import { CONTACT_EMAILS, EMAIL_LINKS } from '@/shared/constants/externalLinks';
import { useUserStore } from '@/stores';
import { dayjs } from '@/utilities';

const Invoices: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(false);

    const [invoices, setInvoices] = useState([]);

    const { selectedUser: user } = useUserStore();
    const { hasMinimumPlan } = useEntitlements();
    const canViewInvoices = hasMinimumPlan('plus');

    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    const handleGetInvoices = useCallback(async () => {
        if (user?.stripeId && canViewInvoices) {
            try {
                setLoading(true);

                await authorizedAxiosInstance
                    .get(`/api/billing/invoices`)
                    .then((res: AxiosResponse) => {
                        if (res?.status === 200) {
                            setInvoices(res?.data);
                        }
                    })
                    .catch((error) => {
                        captureException('Failed to fetch invoices:', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } catch (error) {
                captureException(error);
            }
        }
    }, [canViewInvoices, user?.stripeId]);

    useEffect(() => {
        handleGetInvoices();
    }, []);

    const columns = [
        {
            title: 'Date & Time of Invoice',
            dataIndex: 'date',
            key: 'date',
            render: (date: any) => dayjs(date).format('LLL'),
            align: 'left',
        },
        {
            title: 'Amount (USD)',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: any, record: any) =>
                new Intl.NumberFormat('en-US', { style: 'currency', currency: record.currency }).format(amount),
            align: 'left',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: any) => (
                <Tag color={status === 'paid' ? 'green' : 'red'} style={{ borderColor: 'transparent' }}>
                    {status && capitalCase(status)}
                </Tag>
            ),
            align: 'left',
        },
        {
            title: 'View Invoice',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space size='middle'>
                    {record.hostedInvoiceUrl && (
                        <Button href={record.hostedInvoiceUrl} target='_blank' type='dashed'>
                            View Invoice
                            <TbFileArrowRight size='1rem' style={{ verticalAlign: '-0.2rem' }} />
                        </Button>
                    )}
                </Space>
            ),
            align: 'right',
        },
    ] as any;

    const navigate = useNavigate();

    const { token } = useAntDesignTheme();

    return (
        <>
            <Typography.Title id='settings-account-plans-invoices-section' level={4}>
                Invoices
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                View and download your current & past invoices.
            </Typography.Paragraph>
            {invoices?.length !== 0 && !loading ? (
                <PlatformTable
                    showHeader={true}
                    size='small'
                    loading={loading}
                    columns={columns}
                    dataSource={invoices}
                    rowKey='id'
                    pagination={{ pageSize: 10 }}
                    style={{ border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadius }}
                />
            ) : loading ? (
                <BaseSkeleton title active round paragraph={{ rows: 8 }} />
            ) : (
                <EmptySection
                    height='20rem'
                    icon={<TbFileInvoice style={{ color: token.colorTextTertiary }} />}
                    label='No Invoices'
                    description={
                        <>
                            This account does not have any invoices yet. Please contact us at{' '}
                            <a href={EMAIL_LINKS.billing}>
                                {CONTACT_EMAILS.billing}
                                <TbMailShare style={{ verticalAlign: '-0.1rem', marginLeft: '0.125rem' }} />
                            </a>{' '}
                            if you are running into issues or have questions.
                        </>
                    }
                    extra={
                        <Button onClick={() => navigate(`/settings/account/plans/billing`)}>
                            <TbArrowLeft />
                            Upgrade Plan
                        </Button>
                    }
                />
            )}
            <Divider style={{ margin: '1rem 0' }} />
            <LearnMoreFooter topic='billing' />
        </>
    );
};

export default Invoices;
